import { Card, ListGroup } from "react-bootstrap";

// One shimmering bar, same look as the CustomTable loading rows. Width / height as CSS values; round = pill or circle.
export const Bar = ({ w = "100%", h = 10, round, className = "" }) => (
  <span
    className={`table-skeleton-bone flex-shrink-0 ${round ? "rounded-pill" : ""} ${className}`}
    style={{ width: w, height: h }}
  />
);

// Small label line over a bigger value line
const Field = ({ value = "75%" }) => (
  <div className="flex-column gap-3">
    <Bar w="45%" h={8} />
    <Bar w={value} h={12} />
  </div>
);

// Avatar circle + name line + detail line (contacts, follow-ups)
export const Person = ({ name = "60%", sub = "40%" }) => (
  <div className="d-flex align-items-center gap-5">
    <Bar w={32} h={32} round />
    <div className="flex-column gap-3 flex-grow-1">
      <Bar w={name} h={11} />
      <Bar w={sub} h={8} />
    </div>
  </div>
);

// Card in the same frame as the real panels: title row, then its body
export const PanelSkeleton = ({ title = 90, children }) => (
  <Card className="rounded-4 border shadow-none">
    <div className="flex-between px-7 py-5 border-bottom">
      <Bar w={title} h={12} />
      <Bar w={14} h={14} round />
    </div>
    <div className="px-7 py-6 flex-column gap-7">
      {children || (
        <>
          <Person />
          <Person name="70%" sub="35%" />
          <Person name="55%" sub="45%" />
        </>
      )}
    </div>
  </Card>
);

const RECORD_WIDTHS = ["55%", "40%", "60%", "45%"];

/**
 * Loading view inside a details tab (Notes, Activity, Follow-ups, Quotations…): record cards
 * like the ones the tab shows, shimmering the same way as the page skeleton.
 */
export const RecordsSkeleton = ({ rows = 3 }) => (
  <div className="flex-column gap-8" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, i) => (
      <Card key={i} className="rounded-4 border shadow-none px-7 py-6">
        <div className="d-flex align-items-start gap-6">
          <Bar w={36} h={36} className="rounded-3" />
          <div className="flex-column gap-4 flex-grow-1">
            <div className="flex-between gap-6">
              <Bar w={RECORD_WIDTHS[i % RECORD_WIDTHS.length]} h={12} />
              <Bar w={70} h={18} round />
            </div>
            <Bar w="85%" h={9} />
            <Bar w="30%" h={9} />
          </div>
        </div>
      </Card>
    ))}
  </div>
);

/**
 * Loading view for a details page (shimmer): header, left panels, tabs + records in the middle, panels on the right.
 * Same frame as the real page, so nothing jumps when the data arrives.
 * layoutClassName = the page's own column layout class ("cd-layout" on most pages, "" on tracking).
 * map = one big block in the middle instead of tabs (tracking); right = false drops the right column.
 */
const DetailsSkeleton = ({
  layoutClassName = "cd-layout",
  map = false,
  right = true,
  height = "calc(100vh - 60px)",
}) => (
  <div
    className="flex-column"
    style={{ height }}
    aria-busy="true"
    aria-label="Loading"
  >
    {/* Header: back, name, chips line; buttons on the right */}
    <div className="cd-header bg-light-subtle border-bottom px-12 py-7">
      <div className="d-flex align-items-center gap-6">
        <Bar w={28} h={28} round />
        <div className="flex-column gap-4 flex-grow-1">
          <Bar w={240} h={18} />
          <div className="d-flex align-items-center gap-4">
            <Bar w={80} h={18} round />
            <Bar w={70} h={18} round />
            <Bar w={130} h={10} />
            <Bar w={150} h={10} />
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Bar w={80} h={32} className="rounded-3" />
          {[1, 2, 3, 4].map((i) => (
            <Bar key={i} w={32} h={32} className="rounded-3" />
          ))}
        </div>
      </div>
    </div>

    <div className={`details-page ${layoutClassName} px-12 overflow-hidden`}>
      {/* LEFT */}
      <div className="left-section w-100 overflow-hidden flex-column gap-10 py-10 ps-1 pe-10 border-end">
        <PanelSkeleton title={80}>
          <div className="grid columns-2 gap-8">
            {["70%", "80%", "60%", "75%", "65%", "85%"].map((w, i) => (
              <Field key={i} value={w} />
            ))}
          </div>
        </PanelSkeleton>
        <PanelSkeleton title={70}>
          <Person />
          <Person name="50%" sub="65%" />
        </PanelSkeleton>
        <PanelSkeleton title={85}>
          <Bar w={60} h={16} round />
          <Bar w="90%" h={10} />
          <Bar w="65%" h={10} />
        </PanelSkeleton>
      </div>

      {/* MIDDLE: tab strip, then record cards (or the map) */}
      {map ? (
        <div className="middle-section-wrapper flex-column py-10 ps-10 h-100 overflow-hidden">
          <Bar w="100%" h="100%" className="rounded-4" />
        </div>
      ) : (
        <div className="middle-section-wrapper flex-column gap-8 py-10 h-100 overflow-hidden">
          <div className="d-flex align-items-center gap-9 border-bottom pb-5">
            {[60, 75, 90, 65, 80, 55].map((w, i) => (
              <Bar key={i} w={w} h={12} />
            ))}
          </div>
          <RecordsSkeleton rows={4} />
        </div>
      )}

      {/* RIGHT */}
      {right && (
        <div className="right-section-summary overflow-hidden py-10 ps-10 pe-1 border-start flex-column gap-10">
          <PanelSkeleton title={130} />
        </div>
      )}
    </div>
  </div>
);

/**
 * Rows of a settings list (Settings, Product Settings) while it loads: a name bar and the two
 * action icons, in the same ListGroup row frame as the real rows.
 */
const LIST_ROW_WIDTHS = ["38%", "52%", "30%", "45%", "34%"];
export const ListRowsSkeleton = ({ rows = 5 }) => (
  <div aria-busy="true" aria-label="Loading">
    {LIST_ROW_WIDTHS.slice(0, rows).map((w) => (
      <ListGroup.Item key={w} className="py-6 px-8 flex-between gap-8">
        <Bar w={w} h={12} />
        <span className="d-flex gap-6">
          <Bar w={16} h={16} className="rounded-2" />
          <Bar w={16} h={16} className="rounded-2" />
        </span>
      </ListGroup.Item>
    ))}
  </div>
);

/**
 * A page body while it loads: an optional title bar, a row of stat cards, then a card with
 * table-like rows. Used where the page has no fixed shape of its own (reports tab, org switch).
 */
export const PageSkeleton = ({ title = false, cards = 4, rows = 6, className = "" }) => (
  <div className={`flex-column gap-8 ${className}`} aria-busy="true" aria-label="Loading">
    {title && <Bar w={220} h={22} />}
    <div className="row g-6">
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="col-12 col-sm-6 col-xl-3">
          <Card className="rounded-4 border shadow-none p-8 flex-column gap-4">
            <Bar w="55%" h={10} />
            <Bar w="35%" h={22} />
            <Bar w="70%" h={9} />
          </Card>
        </div>
      ))}
    </div>
    <Card className="rounded-4 border shadow-none p-8 flex-column gap-6">
      <Bar w={180} h={14} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="d-flex align-items-center gap-8 pt-6 border-top">
          <Bar w={28} h={28} round />
          <Bar w={["30%", "22%", "26%", "34%"][i % 4]} h={11} />
          <Bar w="12%" h={11} className="ms-auto" />
          <Bar w="10%" h={11} />
        </div>
      ))}
    </Card>
  </div>
);

/** Table rows while a <table> loads: one bar per cell. cols = number of columns. */
export const TableRowsSkeleton = ({ cols, rows = 4 }) =>
  Array.from({ length: rows }, (_, r) => (
    <tr key={r} aria-hidden="true">
      {Array.from({ length: cols }, (_, c) => (
        <td key={c}>
          <Bar w={c === 0 ? "70%" : "55%"} h={11} className={c === 0 ? "" : "mx-auto"} />
        </td>
      ))}
    </tr>
  ));

export default DetailsSkeleton;
